import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { Layer, Source, useMap } from "react-map-gl";
import { useDrawMapStore, useLeadStore, useMapStore } from "../..";
import useGetLeadColor from "../../hooks/useGetLeadColor";
import useHasReminder from "../../hooks/useHasReminder";
import useLeadsWithFocusArea from "../../hooks/useLeadsWithFocusArea";
import useLeadsWithGlobalFilters from "../../hooks/useLeadsWithGlobalFilters";
import useNavigateToLead from "../../hooks/useNavigateToLead";
import ILayerProps from "../../interfaces/IlayerProps";
import { minZoom } from "../mapConfig";

export const leadMarkerLayerId = "leadMarkerLayer";

function LeadsMapLayer({ visible }: ILayerProps) {
    const navigateToLead = useNavigateToLead();
    const mapStore = useMapStore();
    const leadStore = useLeadStore();
    const drawMapStore = useDrawMapStore();
    const map = useMap().current!;
    const leads = useLeadsWithFocusArea(useLeadsWithGlobalFilters(leadStore.leads));
    const getLeadColor = useGetLeadColor();
    const hasReminder = useHasReminder();

    useEffect(() => {
        map.loadImage("./elipse-small.png", (error, image) => {
            if (error) throw error;
            // add image to the active style and make it SDF-enabled
            map.addImage("ico-circle", image!, { sdf: true });
        });

        map.loadImage("./diamond-small.png", (error, image) => {
            if (error) throw error;
            map.addImage("ico-diamond", image!, { sdf: true });
        });
    }, []);

    useEffect(() => {
        const unsubscribeMouseClick = mapStore.onMouseClick("leads", feature => {
            if (feature?.layer?.id === leadMarkerLayerId && !drawMapStore.isActiveDrawing())
                navigateToLead(Number(feature.id));
        });

        return unsubscribeMouseClick;
    }, []);

    const leadMarkersGeoJson: any = useMemo(() => computed(() => ({
        type: "FeatureCollection",
        features: leads.map(l => ({
            type: "Feature",
            id: l.id,
            geometry: {
                type: "Point",
                coordinates: [l.centerPoint.longitude, l.centerPoint.latitude],
            },
            properties: {
                color: getLeadColor(l.status),
                hasReminder: hasReminder(l),
                hasExcelAttachments: l.hasExcelAttachments,
            },
        })),
    })), [leads, getLeadColor, hasReminder]).get();

    if (!mapStore.isStyleLoaded)
        return null;

    return (
        <Source id="leadMarkerSource" type="geojson" data={leadMarkersGeoJson}>
            <Layer
                id="leadMarkerHaloLayer"
                type="symbol"
                paint={{ "icon-color": "white" }}
                minzoom={minZoom.leads}
                layout={{
                    "icon-size": 1,
                    "icon-allow-overlap": true,
                    "symbol-z-order": "source",
                    "icon-image": ["case", ["get", "hasExcelAttachments"], "ico-diamond", "ico-circle"],
                    "visibility": visible ? "visible" : "none",
                }}
            />
            <Layer
                id={leadMarkerLayerId}
                type="symbol"
                paint={{ "icon-color": ["get", "color"] }}
                minzoom={minZoom.leads}
                layout={{
                    "icon-size": 0.9,
                    "icon-allow-overlap": true,
                    "symbol-z-order": "source",
                    "icon-image": ["case", ["get", "hasExcelAttachments"], "ico-diamond", "ico-circle"],
                    "visibility": visible ? "visible" : "none",
                }}
            />
            <Layer
                id="leadMarkerReminderBorderLayer"
                type="symbol"
                paint={{ "icon-color": ["case", ["get", "hasReminder"], "#ffffff", "white"] }}
                minzoom={minZoom.leads}
                layout={{
                    "icon-size": 0.60,
                    "icon-allow-overlap": true,
                    "symbol-z-order": "source",
                    "icon-image": ["case", ["get", "hasExcelAttachments"], "ico-diamond", "ico-circle"],
                    "visibility": visible ? "visible" : "none",
                }}
            />
            <Layer
                id="leadMarkerInnerLayer"
                type="symbol"
                paint={{ "icon-color": ["case", ["get", "hasReminder"], "#FF8C00", "white"] }}
                minzoom={minZoom.leads}
                layout={{
                    "icon-size": 0.45,
                    "icon-allow-overlap": true,
                    "symbol-z-order": "source",
                    "icon-image": ["case", ["get", "hasExcelAttachments"], "ico-diamond", "ico-circle"],
                    "visibility": visible ? "visible" : "none",
                }}
            />
        </Source>
    );
}

export default observer(LeadsMapLayer);